import React, { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { LoginBlock, LoginError } from './LoginBlock';
import AuthService from 'services/auth.service';
import { login } from 'utils/helpers/browser/login';
import { getTranslationKey, useTranslation } from 'utils/localization';
import { getRoute } from 'constants/routes';
import { TNamespaceEnum } from 'types/types';
import {
    MATURED_PERSON_RECOVERY_URL_PATH,
    ANONYMOUS_PAYMENT_URL_PATH,
} from 'constants/general';
//import { extractCookieValues } from 'utils/helpers/browser/cookies';
import { performRecoveryByLogin } from 'services/personRecovery.service';

const Login = (): ReactElement => {
    const { t } = useTranslation();
    const router = useRouter();

    const handleLogin = async (
        email: string,
        password: string
    ): Promise<LoginError> => {
        const response = await new AuthService().login({ email, password });

        if (response.access_token && response.refresh_token) {
            login(response.access_token, response.refresh_token);
            if (response.user.is1_auth_token) {
                /*
                const { cookieName, cookieValue } = extractCookieValues(
                    response.user.is1_auth_token
                );
                document.cookie = `${cookieName}=${cookieValue}; expires=${new Date(
                    new Date().getTime() + 24 * 60 * 60 * 1000
                ).toUTCString()}; path=/; domain=lab.fotbal.cz; SameSite=Lax`;
                 */
            }

            // Default to '/' so the role-based redirect in pages/index.tsx
            // (getLandingUrl) kicks in for the logged-in user. The `return` /
            // `redirect` query param still wins for explicit deep-links.
            let url = getRoute('/');

            // Podporujeme jak 'return' (nový) tak 'redirect' (starý) parametr pro zpětnou kompatibilitu
            if (router.query.return && router.query.return !== '0') {
                const returnUrl = decodeURIComponent(
                    router.query.return as string
                );
                // Security check: Allow external URLs as per requirements
                url = returnUrl;
            } else if (router.query.redirect && router.query.redirect !== '0') {
                const redirectUrl = decodeURIComponent(
                    router.query.redirect as string
                );
                // Security check: Allow external URLs as per requirements
                url = redirectUrl;
            }
            window.pageRedirect(url);
            return;
        }
        if (response?.error === 'is1_user_not_migrated') {
            try {
                const success = await performRecoveryByLogin(
                    email,
                    password,
                    false,
                    router
                );

                if (success) {
                    console.log('Recovery successful');
                }
            } catch (error) {
                console.error('Recovery failed:', error);
            }
            return;
        }

        const conversions: Record<
            string,
            { textKey: string; linkKey?: string; linkHref?: string }
        > = {
            person_recovery_error: {
                textKey: 'error.personRecoveryError',
            },
            user_has_matured: {
                textKey: getTranslationKey(
                    TNamespaceEnum.login,
                    'error.userHasMatured'
                ),
                linkKey: getTranslationKey(
                    TNamespaceEnum.login,
                    'adultProfilePersonRecoveryPage'
                ),
                linkHref: MATURED_PERSON_RECOVERY_URL_PATH,
            },
            not_paid_member: {
                textKey: getTranslationKey(
                    TNamespaceEnum.login,
                    'error.memberNotPaid'
                ),
                linkKey: getTranslationKey(
                    TNamespaceEnum.login,
                    'anonymousPaymentPage'
                ),
                linkHref: ANONYMOUS_PAYMENT_URL_PATH,
            },
            user_not_verified: {
                textKey: getTranslationKey(
                    TNamespaceEnum.landingPage,
                    'error.userNotVerified'
                ),
            },
            user_locked: {
                textKey: 'error.userLocked',
            },
            blocked_user: {
                textKey: getTranslationKey(
                    TNamespaceEnum.login,
                    'error.userBlocked'
                ),
            },
        };

        const converted = conversions[response?.error] ?? {
            textKey: getTranslationKey(
                TNamespaceEnum.login,
                'error.reviewLogInDetails'
            ),
        };

        return {
            message: t(converted.textKey),
            linkContent: converted.linkKey ? t(converted.linkKey) : null,
            linkHref: converted.linkHref,
        };
    };

    return (
        <LoginBlock
            loginCb={handleLogin}
            email={router.query.email as string}
            showForgottenPasswordBlock
        />
    );
};

export default Login;
